import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Avatar,
  Dropdown,
  Badge,
  List
} from 'antd';
import {
  UserOutlined,
  BookOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  ReadOutlined,
  SettingOutlined,
  BarChartOutlined,
  LogoutOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  HomeOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

export default function SuperAdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTrainings: 0,
    activeSessions: 0,
    totalLabs: 0
  });
  const [systemStatus, setSystemStatus] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchSystemStatus();
  }, []);

  const fetchStats = async () => {
    try {
      const [usersRes, trainingsRes, sessionsRes, labsRes] = await Promise.allSettled([
        api.get('/users'),
        api.get('/trainings'),
        api.get('/sessions/active'),
        api.get('/labs')
      ]);

      setStats({
        totalUsers: usersRes.status === 'fulfilled' ? usersRes.value.data.length : 0,
        totalTrainings: trainingsRes.status === 'fulfilled' ? trainingsRes.value.data.length : 0,
        activeSessions: sessionsRes.status === 'fulfilled' ? sessionsRes.value.data.length : 0,
        totalLabs: labsRes.status === 'fulfilled' ? labsRes.value.data.length : 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemStatus = async () => {
    try {
      const response = await api.get('/system/health');
      setSystemStatus(response.data.services || []);
    } catch (error) {
      console.error('Error fetching system status:', error);
      // Set default services if API fails
      setSystemStatus([
        { service: 'API Server', status: 'operational', uptime: 'N/A' },
        { service: 'Database', status: 'operational', uptime: 'N/A' },
        { service: 'WebSocket Server', status: 'operational', uptime: 'N/A' },
        { service: 'AI Service', status: 'operational', uptime: 'N/A' },
      ]);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/super-admin/dashboard'),
    },
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/super-admin/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const quickActions = [
    {
      title: 'Manage Users',
      icon: <TeamOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      onClick: () => navigate('/admin/users'),
    },
    {
      title: 'Training Programs',
      icon: <ReadOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      onClick: () => navigate('/admin/trainings'),
    },
    {
      title: 'System Configuration',
      icon: <SettingOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      onClick: () => navigate('/super-admin/system'),
    },
    {
      title: 'View Reports',
      icon: <BarChartOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      onClick: () => navigate('/super-admin/reports'),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0, color: '#faad14' }}>
            USSEDU Platform
          </Title>
          <Badge count="Super Admin" style={{ backgroundColor: '#faad14', marginLeft: 16 }} />
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>Welcome back, {user?.first_name}!</Title>
          <Text type="secondary">Here's what's happening with your platform today.</Text>
        </div>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Users"
                value={stats.totalUsers}
                loading={loading}
                prefix={<UserOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Training Programs"
                value={stats.totalTrainings}
                loading={loading}
                prefix={<BookOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Active Sessions"
                value={stats.activeSessions}
                loading={loading}
                prefix={<PlayCircleOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Lab Exercises"
                value={stats.totalLabs}
                loading={loading}
                prefix={<ExperimentOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Quick Actions" bordered={false}>
              <Row gutter={[16, 16]}>
                {quickActions.map((action, index) => (
                  <Col xs={24} sm={12} key={index}>
                    <Card
                      hoverable
                      onClick={action.onClick}
                      style={{ textAlign: 'center' }}
                    >
                      <Space direction="vertical" size="small">
                        {action.icon}
                        <Text strong>{action.title}</Text>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="System Status" bordered={false}>
              <List
                dataSource={systemStatus}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <Badge
                          status={item.status === 'operational' ? 'success' : 'error'}
                          dot
                        />
                      }
                      title={item.service}
                      description={`Uptime: ${item.uptime}`}
                    />
                    {item.status === 'operational' ? (
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                    ) : (
                      <ClockCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                    )}
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
}
